import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { api } from "../../lib/api";
import { Button, Field, Input, Select, Segmented, Pill, DifficultyPicker } from "../ui/ds";
import { DEFAULT_DIFFICULTY, DIFF_SK, normDifficulty } from "../../lib/points";
import { session } from "../../lib/session";
import { st } from "../../styles.js";
import { Icon } from "../../icons";
import { BuzerIcon } from "../../BuzerIcons";
import { Modal, FormShell, FormFooter, IconPicker, Glyph, Chk, DateField, DateTimeField, FriendChip, FriendPickerHint, useModalBackClose, useOverlayZ } from "../ui";
import { IconColorModal } from "./IconColorModal";
import { RecurrenceField } from "./RecurrenceField";
import { PrevRoundField } from "./PrevRoundField";
import { ReminderPrompt } from "./ReminderPrompt";
import { SUGGEST_ICONS, DUR_PRESETS, DUR_OPTIONS, APP_THEME_SWATCHES, IS_ANDROID, LIFE_CATS, LIFE_CAT, RECUR_PERIOD_UNIT, START_MAX_AHEAD_DAYS } from "../../lib/constants";
import { iconChipStyle } from "../../lib/icons-resolve";
import { OFFSETS, shiftDay, todayKey, unitIsTime } from "../../lib/format";
import { fmtDuePretty } from "../../lib/date-sk";

// v29 §1.2 — AI odpoveď → náročnosť. Nový `ai.estimate_habit` má vracať rovno `difficulty`;
// kým sa nenasadí (alebo keď príde staršia odpoveď z cache), odvodíme ju z pôvodných bodov.
// TODO(v29-open): hranice mapovania bodov na náročnosť potvrdiť (docs/v29-open-questions.md).
function aiDifficulty(r) {
  if (r && r.difficulty) return normDifficulty(r.difficulty);
  const p = Number(r && r.points) || 0;
  if (!p) return null;
  return p <= 7 ? "easy" : p <= 15 ? "normal" : "hard";
}

// v2 — small centered confirm dialog for the AI sparks (invent habit / estimate difficulty)
function AiCenterDialog({ onClose, children }) {
  useModalBackClose(onClose);
  // f-batch #1 — tretí typ prekrytia v appke ide do TOHO ISTÉHO počítadla hladín ako Modal/Sheet
  // (predtým mal natvrdo zIndex 400 — nad všetkým vrátane oslavy dňa a konfiet). Otvára sa vždy
  // z formulára, takže dostane hladinu nad ním.
  const overlayZ = useOverlayZ();
  return createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: overlayZ, background: IS_ANDROID ? "rgba(0,0,0,.6)" : "rgba(0,0,0,.5)" /* token-exempt: Android/Mali scrim — opaque, bez backdrop-filter (§9) */, backdropFilter: IS_ANDROID ? "none" : "blur(8px)", WebkitBackdropFilter: IS_ANDROID ? "none" : "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "fadeIn .2s ease" }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" style={{ width: 340, maxWidth: "100%", background: "var(--neutral-surface)", borderRadius: "var(--radius-xl)", boxShadow: "var(--elev-3)", overflow: "hidden" }}>
        {children}
      </div>
    </div>, document.body);
}

// Single entry point for every task form. Reminders/events render the PlanForm
// body; habits and one-time tasks render HabitCard. One import, one place to wire.
export function AddCard(props) {
  if (props.kind === "reminder") {
    return <PlanForm editTask={props.task} reload={props.onAdded} onEditDone={props.onCancel}
      categories={props.categories} prefill={props.prefill} open={props.open} onOpenChange={props.onOpenChange} />;
  }
  return <HabitCard {...props} />;
}

// v38 — `fields` zúži formulár na vymenované polia (bez propu je celý, ako doteraz).
// Platné kľúče: name · spec · category · difficulty · timer · group · recur · duration
// · remind · public · oneday — plus `ai`, čo nie je pole, ale iskrička „vymyslieť celý
// návyk" v hlavičke (rovnaký prepínač, aby zúžený formulár nepotreboval druhý prop).
// Knižnica návykov (`HabitLibrarySheet`) posiela šesticu bez `ai`: názov a špecifikáciu
// si človek doladí, ale vymýšľať celý návyk v knižnici nemá zmysel — obsah kurátoruje
// majiteľka a AI by vybraný návrh prepísala na niečo úplne iné.
function HabitCard({ kind, onAdded, cta, task, initial, onCancel, categories, open: openProp, onOpenChange, routines, lite, onDelete, fields, title: titleProp, subtitle }) {
  const editing = !!task;
  // `fields` = zúžený formulár (knižnica návykov). Bez propu ostáva všetko po starom.
  const show = (k) => !fields || fields.indexOf(k) >= 0;
  // v21 §1b — `initial` prefills a CREATE form (e.g. AI-proposed habit, edited before it's created).
  const src = task || initial || null;
  const dueInit = task && task.due_at
    ? (kind === "reminder" ? task.due_at.replace(" ", "T") : task.due_at) : "";
  // open can be controlled by a parent (so only one create form is expanded at a time);
  // drafts live in this component's own state, so collapsing never loses unsaved input.
  const controlled = openProp !== undefined;
  const [openState, setOpenState] = useState(editing || !!initial);
  const open = controlled ? openProp : openState;
  const setOpen = (v) => { if (controlled) { onOpenChange && onOpenChange(v); } else setOpenState(v); };
  const [icon, setIcon] = useState(src ? (src.icon || "") : (kind === "habit" ? "target" : ""));
  const [iconColor, setIconColor] = useState(src ? (src.icon_color || "") : "");  // v2 — icon bg colour
  const [iconPick, setIconPick] = useState(false);  // v8 BuzerIcon picker (habits)
  const [label, setLabel] = useState(src ? (src.label || "") : "");
  const [desc, setDesc] = useState(src ? (src.desc || "") : "");
  // v29 §1.2 — namiesto voľného číselníka „Body" sa vyberá NÁROČNOSŤ (body sú z nej odvodené)
  const [difficulty, setDifficulty] = useState(normDifficulty(src && src.difficulty));
  const _initSec = (src && (src.timer_sec || (src.timer_min || 0) * 60)) || 0;
  const [timerMin, setTimerMin] = useState(Math.floor(_initSec / 60));
  const [timerSec, setTimerSec] = useState(_initSec % 60);
  const [oneday, setOneday] = useState(src ? !!src.boost : false);  // one-day boost
  // H3 — habit recurrence
  const [recurMode, setRecurMode] = useState(src ? (src.recur_mode || "daily") : "daily");
  const [duration, setDuration] = useState(src && src.duration_days ? src.duration_days : 0);  // v21 — default Bez limitu (0); 7-default archivoval návyky pri úprave
  // v33 §7 — návyk môže nadväzovať na ARCHIVOVANÝ návyk (reštart po pauze, zmena formy)
  const [prevTaskId, setPrevTaskId] = useState(null);
  // ZAČIATOK — návyk sa na Dnes objaví až v tento deň (predvolene dnes). Len pri TVORBE:
  // pri úprave by posun začiatku prepísal `created_at`, a tým aj to, odkedy sa počíta história.
  // Pri TVORBE predvolene dnes; pri ÚPRAVE nezačatého návyku jeho zvolený štart (dá sa
  // opraviť — nemá ešte žiadnu históriu). Bežiaci návyk pole nedostane vôbec.
  const [startDate, setStartDate] = useState((src && src.upcoming && src.start) || todayKey());
  const [durCustom, setDurCustom] = useState(!!(src && src.duration_days && !DUR_PRESETS.includes(src.duration_days)));
  const [recurN, setRecurN] = useState(src && src.recur_n ? src.recur_n : 2);
  const [recurDays, setRecurDays] = useState(src && src.recur_days ? src.recur_days.split(",").map(Number) : []);
  const [recurPeriod, setRecurPeriod] = useState(src && src.recur_period ? src.recur_period : "week");  // v33b — obdobie pre 'period'/'weekly_count'
  const [perDay, setPerDay] = useState(src && src.per_day ? src.per_day : 1);  // v33b — Viackrát za deň (1 = raz)
  // MERATEĽNÝ NÁVYK („vlastný výsledok"): jednotka + nepovinný denný cieľ. Keď je hodnota ČAS,
  // drží sa (rovnako ako výsledok súťaže so stopkami) v SEKUNDÁCH, a cieľ sa zadáva v dvoch
  // políčkach min/s — presne ako Časovač vyššie, aby „20" nikdy neznamenalo 20 sekúnd.
  const _mTarget = Number((src && src.metric_target) || 0);
  const [metricOn, setMetricOn] = useState(!!(src && src.metric_label));
  // DENNÍK §8 — návyk „splní sa zápismi do denníka": koľko zápisov dňa ho splní (1–5, predvolené 3).
  // 0 = bežný návyk. Výlučné s vlastným výsledkom / stopkami / časovačom / viackrát za deň.
  const [entryGoal, setEntryGoal] = useState(Number(src && src.entry_goal) || 0);
  const entryOn = entryGoal > 0;
  const [metricLabel, setMetricLabel] = useState((src && src.metric_label) || "");
  const [metricTime, setMetricTime] = useState(!!(src && src.metric_time));
  const [metricTimer, setMetricTimer] = useState(!!(src && src.metric_timer));  // stopky merajú výsledok
  // kým sa prepínača „Toto je čas" nedotkne človek, riadi ho jednotka („minút" → čas)
  const [metricTimeSet, setMetricTimeSet] = useState(!!(src && src.metric_label));
  const [metricTarget, setMetricTarget] = useState(_mTarget && !(src && src.metric_time) ? String(_mTarget) : "");
  const [metricTargetMin, setMetricTargetMin] = useState(src && src.metric_time ? Math.floor(_mTarget / 60) : 0);
  const [metricTargetSec, setMetricTargetSec] = useState(src && src.metric_time ? Math.round(_mTarget % 60) : 0);
  const [remindAt, setRemindAt] = useState(src && src.remind_at ? src.remind_at : "");  // v25 §8 — pripomenutie návyku
  // v8 — routine/category assignment (habits only)
  const [catId, setCatId] = useState(src && src.category_id != null ? String(src.category_id) : (initial && initial.pendingRoutine ? "__new__" : ""));
  const [pendingRoutine, setPendingRoutine] = useState(initial && initial.pendingRoutine ? initial.pendingRoutine : null);  // v9 AI-proposed NEW routine {name, icon}, created on save
  const [routineIconPick, setRoutineIconPick] = useState(false);  // v20 — vybrať ikonu novej rutiny
  const [skupInfo, setSkupInfo] = useState(false);  // v33c — „i" vysvetlenie čo je Skupina
  const [due, setDue] = useState(dueInit);
  const [repeat, setRepeat] = useState(task ? (task.repeat || "none") : "none");
  const [offs, setOffs] = useState(task && task.notify_offsets ? task.notify_offsets : [120]);
  const [shareWith, setShareWith] = useState(task && task.share_with ? task.share_with : []);
  const [friends, setFriends] = useState([]);
  // v32 — životná kategória sa berie z `src`, nie len z `task`: predvyplnený formulár
  // (návrh z knižnice, AI draft) ju posiela v `initial.category` a doteraz sa ticho
  // zahodila — návyk pridaný z knižnice vznikol bez kategórie.
  const [category, setCategory] = useState(src ? (src.category || "") : "");
  const [catBusy, setCatBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [descBusy, setDescBusy] = useState(false);   // v9 — description-only AI button
  const [showMore, setShowMore] = useState(!!editing || !!initial);  // v9 — collapse advanced fields for new items
  const [iconAiBusy, setIconAiBusy] = useState(false);  // v2 — AI spark in the icon-picker header
  const [inventOpen, setInventOpen] = useState(false);  // v2 — AI dialog A: vymyslieť celý návyk
  const [ptsDialog, setPtsDialog] = useState(null);     // v2 — AI dialog B: { busy } | { level }
  const [lifeCatDialog, setLifeCatDialog] = useState(null);  // v32 — AI návrh kategórie: { busy } | { category }
  const [publicProgress, setPublicProgress] = useState(src ? !!src.public_progress : false);  // v32 — progres vo verejnom profile
  const [remindPrompt, setRemindPrompt] = useState(null);  // v2 — po uložení ponúkni pripomienku {taskId,label}

  // load friend list for the reminder share picker
  useEffect(() => {
    if (kind === "reminder" && open) {
      api("/api/friends").then((d) => setFriends(d.friends || [])).catch(() => {});
    }
  }, [kind, open]);
  function toggleShare(id) {
    setShareWith((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  }

  function reset() {
    setIcon(""); setIconColor(""); setLabel(""); setDesc(""); setDifficulty(DEFAULT_DIFFICULTY); setTimerMin(0); setTimerSec(0); setOneday(false);
    setRecurMode("daily"); setRecurN(2); setRecurDays([]); setRecurPeriod("week"); setPerDay(1);
    setDue(""); setRepeat("none"); setOffs([120]); setShareWith([]); setCategory(""); setErr("");
    setCatId(""); setPendingRoutine(null);
    setMetricOn(false); setMetricLabel(""); setMetricTime(false); setMetricTimeSet(false); setMetricTimer(false);
    setMetricTarget(""); setMetricTargetMin(0); setMetricTargetSec(0);
  }
  async function aiCategory() {
    if (!label.trim()) { setErr("Najprv napíš názov."); return; }
    setErr(""); setCatBusy(true);
    try {
      const r = await api("/api/ai/suggest-category", { method: "POST", body: { label } });
      if (r.category) setCategory(r.category);
    } catch (e) { setErr(e.message); }
    setCatBusy(false);
  }
  function close() {
    // Close via the parent's onCancel when it manages the form (edit / AI draft);
    // otherwise (controlled add form — incl. a preset `initial` category) collapse it.
    if (onCancel) { onCancel(); }
    else { reset(); setOpen(false); }
  }
  // v9 — one call to the suggest endpoint with whatever's already typed (name + desc)
  async function aiSuggest() {
    const ep = kind === "reminder" ? "/api/ai/suggest-reminder" : "/api/ai/estimate-habit";
    return api(ep, { method: "POST", body: { label, icons: SUGGEST_ICONS, desc } });
  }
  // "all fields" button next to the name → label + icon + desc (+ points/recur/routine for habit)
  // v15 §3 — with no title on a habit, invent the whole habit from the user's memory.
  async function aiFill() {
    if (!label.trim() && kind === "reminder") { setErr("Najprv napíš názov."); return; }
    setErr(""); setAiBusy(true);
    try {
      const r = !label.trim()
        ? ((await api("/api/ai/propose-habit", { method: "POST", body: { icons: SUGGEST_ICONS } })).habit || {})
        : await aiSuggest();
      if (r.label) setLabel(r.label);
      if (r.icon) setIcon(r.icon);
      if (r.desc !== undefined) setDesc(r.desc);
      if (kind !== "reminder") {
        const d = aiDifficulty(r);
        if (d) setDifficulty(d);
        if (r.recur) setRecurSel(r.recur);
        const ro = r.routine || {};
        if (ro.mode === "existing" && ro.id != null) { setPendingRoutine(null); setCatId(String(ro.id)); }
        else if (ro.mode === "new" && ro.name) { setPendingRoutine({ name: ro.name, icon: ro.icon || "folder" }); setCatId("__new__"); }
        else { setPendingRoutine(null); setCatId(""); }
      }
    } catch (e) { setErr(e.message); }
    setAiBusy(false);
  }
  // v2 — AI dialog A: always invent a fresh habit (propose-habit), then fill the form
  async function doInvent() {
    setErr(""); setAiBusy(true);
    try {
      const r = (await api("/api/ai/propose-habit", { method: "POST", body: { icons: SUGGEST_ICONS } })).habit || {};
      if (r.label) setLabel(r.label);
      if (r.icon) setIcon(r.icon);
      if (r.desc !== undefined) setDesc(r.desc);
      const d = aiDifficulty(r);
      if (d) setDifficulty(d);
      if (r.recur) setRecurSel(r.recur);
      const ro = r.routine || {};
      if (ro.mode === "existing" && ro.id != null) { setPendingRoutine(null); setCatId(String(ro.id)); }
      else if (ro.mode === "new" && ro.name) { setPendingRoutine({ name: ro.name, icon: ro.icon || "folder" }); setCatId("__new__"); }
      setInventOpen(false);
    } catch (e) { setErr(e.message); }
    setAiBusy(false);
  }
  // v32 — AI návrh životnej kategórie (rovnaký potvrdzovací popup ako pri bodoch)
  async function openLifeCatDialog() {
    if (!label.trim()) { setErr("Najprv napíš názov návyku."); return; }
    setErr(""); setLifeCatDialog({ busy: true });
    try {
      const r = await api("/api/ai/suggest-life-category", { method: "POST", body: { label, desc } });
      if (!r.category) { setErr("AI nevedela kategóriu určiť — vyber ju ručne."); setLifeCatDialog(null); return; }
      setLifeCatDialog({ category: r.category });
    } catch (e) { setErr(e.message); setLifeCatDialog(null); }
  }
  // v2 — AI dialog B: fetch an estimate, show it, apply on confirm (v29: náročnosť, nie body)
  async function openPtsDialog() {
    if (!label.trim()) { setErr("Najprv napíš názov návyku."); return; }
    setErr(""); setPtsDialog({ busy: true });
    try { const r = await aiSuggest(); setPtsDialog({ level: aiDifficulty(r) || DEFAULT_DIFFICULTY }); }
    catch (e) { setErr(e.message); setPtsDialog(null); }
  }
  // description-only button → fills just the popis from name (+ existing desc as context)
  async function aiDesc() {
    if (!label.trim()) { setErr("Najprv napíš názov."); return; }
    setErr(""); setDescBusy(true);
    try { const r = await aiSuggest(); if (r.desc) setDesc(r.desc); } catch (e) { setErr(e.message); }
    setDescBusy(false);
  }
  function toggleOff(m) {
    setOffs((p) => p.includes(m) ? p.filter((x) => x !== m) : [...p, m]);
  }
  // H3 — map the recurrence picker <select> to (mode, n)
  const recurSel = recurMode === "interval" ? (recurN === 3 ? "i3" : "i2")
    : recurMode === "weekly_count" ? "wc" : recurMode === "weekdays" ? "wd" : "daily";
  function setRecurSel(v) {
    if (v === "i2") { setRecurMode("interval"); setRecurN(2); }
    else if (v === "i3") { setRecurMode("interval"); setRecurN(3); }
    else if (v === "wc") { setRecurMode("weekly_count"); setRecurN((n) => (n >= 1 && n <= 6 ? n : 2)); }
    else if (v === "wd") { setRecurMode("weekdays"); }
    else setRecurMode("daily");
  }
  async function submit(e) {
    e.preventDefault();
    if (!label.trim()) { setErr("Najprv napíš názov."); return; }  // validate on submit, never block the button
    if (kind === "reminder" && !due) { setErr("Vyber termín."); return; }  // v26.3 §1 — DateField nemá natívne required
    if (kind === "habit" && metricOn && !entryOn && !metricLabel.trim()) { setErr("Napíš, čo meriaš — napr. minút, strán, l."); return; }
    setErr(""); setBusy(true);
    try {
      // v9 — if the AI proposed a NEW routine, create it now (on save) so a cancelled draft leaves no orphan
      let effectiveCatId = catId && catId !== "__new__" ? Number(catId) : null;
      if (kind === "habit" && catId === "__new__" && pendingRoutine && (pendingRoutine.name || "").trim()) {
        const cr = await api("/api/categories", { method: "POST", body: { name: pendingRoutine.name.trim(), icon: pendingRoutine.icon } });
        if (cr && cr.id) effectiveCatId = Number(cr.id);
      }
      // v29 §1.2 — body sa už nezadávajú, odvodzujú sa z náročnosti (server: DIFF_POINTS).
      // Lite formulár náročnosť neukazuje (nemá body) → ide default „normálny", takže po
      // prepnutí späť do plného režimu má návyk zmysluplné body bez AI dopytu.
      let body = { kind, icon, label, desc };
      if (kind === "habit") body = { ...body, difficulty, timer_sec: entryOn ? 0 : (Number(timerMin) || 0) * 60 + (Number(timerSec) || 0),
        entry_goal: entryOn ? entryGoal : 0,   // DENNÍK §8
        // v33b — obdobie + Viackrát za deň. Merateľný návyk „viackrát za deň" nepotrebuje —
        // opakovanie počas dňa je už v nazbieranej hodnote (3 poháre = 3 zápisy), dva súbežné
        // pokroky na jednej fajke by si odporovali.
        recur_mode: recurMode, recur_n: recurN, recur_days: recurDays, recur_period: recurPeriod,
        per_day: (recurMode === "daily" && !metricOn && !entryOn) ? (Number(perDay) || 1) : 1,
        category_id: effectiveCatId, duration_days: Number(duration) || 0, icon_color: iconColor || null,
        prev_task_id: prevTaskId,  // v33 — na ktorý archivovaný návyk nadväzuje (null = samostatný)
        remind_at: remindAt || null, category: category || null, public_progress: publicProgress,  // v25 §8 · v32 kategória+verejný progres
        // merateľný návyk: bez jednotky sa režim vypína (server si zároveň vynuluje cieľ)
        metric_label: (metricOn && !entryOn) ? metricLabel.trim() : "",
        metric_time: metricOn && !entryOn && (metricTime || metricTimer),
        metric_timer: metricOn && !entryOn && metricTimer,
        metric_target: (!metricOn || entryOn) ? 0
          : (metricTime ? (Number(metricTargetMin) || 0) * 60 + (Number(metricTargetSec) || 0)
            : Number(String(metricTarget).replace(",", ".")) || 0) };
      else if (kind === "onetime") body = { ...body, due_at: due, category }; // 'YYYY-MM-DD'
      else body = { ...body, due_at: due ? due.replace("T", " ") : "", repeat, notify_offsets: offs, share_with: shareWith };
      if (kind === "habit") body.oneday = oneday;  // v2 — boost applies on create AND edit
      // Začiatok posiela tvorba a ÚPRAVA NEZAČATÉHO návyku (ten históriu ešte nemá, takže
      // sa dá opraviť). Pri bežiacom by prepísal created_at, teda aj to, odkedy beží história —
      // server to preto aj tak odmietne prijať.
      if (kind === "habit" && startDate && (src && src.upcoming ? true : (!editing && startDate !== todayKey()))) {
        body.start_date = startDate;
      }
      let newId = null;
      if (editing) {
        await api("/api/tasks/" + task.id, { method: "PATCH", body });
      } else {
        const r = await api("/api/tasks", { method: "POST", body });
        newId = r && r.id;
      }
      // v2 — nový návyk bez pripomienky → ponúkni jej nastavenie. Zavretie formulára a
      // onAdded/onCancel sa ODKLADAJÚ až po popupe — rodičia na ne často unmountujú
      // (AI draft v App, key-remount v Dnes) a popup by inak zmizol skôr, než sa ukáže.
      const offer = (!editing && kind === "habit" && newId && !remindAt) ? { taskId: newId, label } : null;
      if (!editing) reset();
      if (offer) { setRemindPrompt(offer); setBusy(false); return; }
      setOpen(false); onAdded(newId); onCancel && onCancel();
    } catch (e) { setErr(((e && e.message) || "Nastala chyba") + " — skús znova."); }
    setBusy(false);
  }
  // v2 — pripomienkový popup: kým je otvorený, formulár sa nezatvára (odložené callbacky vyššie)
  const remindPromptEl = remindPrompt ? (
    <ReminderPrompt taskId={remindPrompt.taskId} label={remindPrompt.label}
      onClose={() => { const id = remindPrompt.taskId; setRemindPrompt(null); setOpen(false); onAdded(id); onCancel && onCancel(); }} />
  ) : null;
  // Controlled forms (opened via the FAB / edit-mode buttons) render nothing when closed —
  // no leftover toggle button at the bottom of Dnes. Uncontrolled forms keep the toggle.
  if (!open) return <>{remindPromptEl}{controlled ? null : <button onClick={() => setOpen(true)} style={st.addToggle}><BuzerIcon name="add" size={16} /> {cta}</button>}</>;
  const inModal = open || editing;  // v10 — editing also opens as a modal (not inline)
  // v38 — knižnica návykov si nadpis prepíše na názov vybraného návrhu (formulár už nemá
  // pole „Názov", takže by inak nebolo vidieť, čo sa vlastne pridáva).
  const titleStr = titleProp || (editing
    ? { habit: "Upraviť návyk", onetime: "Upraviť úlohu", reminder: "Upraviť pripomienku" }[kind]
    : { habit: "🔥 Pridať návyk", onetime: "📌 Pridať úlohu", reminder: "🔔 Pridať pripomienku" }[kind]);
  const headerAi = session.ai && kind === "habit" && !editing && show("name") && show("ai") ? (
    <button type="button" onClick={() => setInventOpen(true)} title="Vymyslieť návyk (AI)" aria-label="Vymyslieť návyk"
      style={{ border: "none", background: "transparent", color: "var(--accent)", display: "inline-flex", alignItems: "center", cursor: "pointer", padding: 4 }}>
      <BuzerIcon name="sparkles" size={18} color="currentColor" />
    </button>
  ) : null;

  return (
    <>
    {remindPromptEl}
    <FormShell modal={inModal} title={titleStr} subtitle={subtitle} icon={{habit:"flame",onetime:"clipboard",reminder:"bell"}[kind]} onClose={close} headerAction={inModal ? headerAi : null}
      iconNode={kind === "habit" ? <Glyph icon={icon || "target"} size={20} /> : undefined}
      iconStyle={kind === "habit" ? iconChipStyle(iconColor) : undefined}
      onIconClick={kind === "habit" && show("name") ? () => setIconPick(true) : undefined}>
    <form onSubmit={submit} className={inModal ? "bz-modal-form" : undefined} style={inModal ? undefined : { ...st.card, marginTop: 12, ...st.formCol }}>
      {show("name") && (
      <Field label="Názov *">
        {kind === "habit" ? (
          <Input placeholder="Názov" value={label} onChange={(e) => setLabel(e.target.value)} required />
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <Input style={{ width: 60, textAlign: "center" }} placeholder={{ onetime: "📌", reminder: "🔔" }[kind]}
              value={icon} onChange={(e) => setIcon(e.target.value)} maxLength={4} aria-label="ikona" />
            <Input style={{ flex: 1 }} placeholder="Názov" value={label} onChange={(e) => setLabel(e.target.value)} required />
          </div>
        )}
        {iconPick && (
          <IconColorModal icon={icon} iconColor={iconColor} fallback="target"
            onIcon={setIcon} onColor={setIconColor} onClose={() => setIconPick(false)}
            onCommit={editing ? async (ic, col) => { await api("/api/tasks/" + task.id, { method: "PATCH", body: { icon: ic, icon_color: col || null } }); onAdded && onAdded(); } : undefined}
            onAiIcon={session.ai ? async () => { if (!label.trim()) { setErr("Najprv napíš názov."); return; } return (await aiSuggest()).icon; } : undefined} />
        )}
      </Field>
      )}

      {kind === "habit" && (
        <>
          {/* ŠPECIFIKÁCIA — bez AI tlačidla */}
          {show("spec") && (
          <Field label="Špecifikácia">
            <Input placeholder="napr. 250 ml" value={desc} onChange={(e) => setDesc(e.target.value)} />
          </Field>
          )}
          {/* v32 — ŽIVOTNÁ KATEGÓRIA (pevná sada 8) + AI návrh (malá iskrička pri nadpise) */}
          {show("category") && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>
              <span className="bz-field__label" style={{ marginBottom: 0 }}>Kategória</span>
              {session.ai && <button type="button" onClick={openLifeCatDialog} title="Navrhnúť kategóriu (AI)" aria-label="Navrhnúť kategóriu"
                style={{ border: "none", background: "transparent", color: "var(--accent)", display: "inline-flex", cursor: "pointer", padding: 0 }}><BuzerIcon name="sparkles" size={15} color="currentColor" /></button>}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {LIFE_CATS.map((c) => (
                <Pill key={c.key} on={category === c.key} onClick={() => setCategory(category === c.key ? "" : c.key)}>
                  <BuzerIcon name={c.icon} size={15} color="currentColor" />{c.label}
                </Pill>
              ))}
            </div>
          </div>
          )}
          {/* v29 §1.2 — NÁROČNOSŤ (nahradila voľný číselník „Body"): body sú z nej odvodené.
              Lite nemá body, takže sa výber nezobrazuje a platí default „normálny" (§8.3). */}
          {!lite && show("difficulty") && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>
                <span className="bz-field__label" style={{ marginBottom: 0 }}>Náročnosť</span>
                {session.ai && <button type="button" onClick={openPtsDialog} title="Odhad náročnosti (AI)" aria-label="Odhad náročnosti"
                  style={{ border: "none", background: "transparent", color: "var(--accent)", display: "inline-flex", cursor: "pointer", padding: 0 }}><BuzerIcon name="sparkles" size={15} color="currentColor" /></button>}
              </div>
              <DifficultyPicker value={difficulty} onChange={setDifficulty} streak={(task && task.streak) || 0} />
            </div>
          )}
          {/* ZAPISOVANIE — odškrtnutie vs. vlastný výsledok (meditácia v minútach, strany,
              litre vody). Rovnaký prepínač aj rovnaké názvy ako vo formulári výzvy, nech to
              znamená v celej appke to isté. */}
          {show("metric") && (
          <Field label="Zapisovanie">
            <Segmented className="bz-seg--block"
              options={[{ value: "done", label: "Splnenie" }, { value: "result", label: "Vlastný výsledok" }, { value: "entries", label: "Zápisy" }]}
              value={entryOn ? "entries" : metricOn ? "result" : "done"}
              onChange={(v) => {
                if (v === "entries") { setMetricOn(false); setEntryGoal((g) => (g > 0 ? g : 3)); setPerDay(1); if (!remindAt && !editing) setRemindAt("20:00"); }
                else { setEntryGoal(0); setMetricOn(v === "result"); }
              }} />
            {entryOn ? (<>
                {/* DENNÍK §8.1 — splní sa zápismi do denníka (ráta sa každý zápis dňa) */}
                <div style={{ ...st.taskDesc, marginTop: 6, marginBottom: 8 }}>Splní sa zápismi do denníka — ráta sa každý zápis v ten deň, nemusí byť priradený k tomuto návyku.</div>
                <Field label="Koľko zápisov za deň">
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Button variant="ghost" size="sm" aria-label="Menej" onClick={() => setEntryGoal((g) => Math.max(1, g - 1))} disabled={entryGoal <= 1}>−</Button>
                    <span style={{ minWidth: 28, textAlign: "center", fontFamily: "var(--ff-mono)", fontWeight: 800, fontSize: 18 }}>{entryGoal}</span>
                    <Button variant="ghost" size="sm" aria-label="Viac" onClick={() => setEntryGoal((g) => Math.min(5, g + 1))} disabled={entryGoal >= 5}>+</Button>
                    <span style={st.taskDesc}>{entryGoal === 1 ? "zápis" : entryGoal <= 4 ? "zápisy" : "zápisov"}</span>
                  </div>
                </Field>
              </>) : !metricOn
              ? <div style={{ ...st.taskDesc, marginTop: 6 }}>Návyk sa iba odškrtne — hotovo/nehotovo.</div>
              : (<>
                <div style={{ ...st.taskDesc, marginTop: 6, marginBottom: 8 }}>Zapisuješ si číslo za deň (napr. 20 minút meditácie, 30 strán, 2 l vody). Splnené je, keď dosiahneš denný cieľ.</div>
                <Input maxLength={20} placeholder="Čo meriaš? Jednotka — napr. minút, strán, l"
                  value={metricLabel}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMetricLabel(v);
                    if (!metricTimeSet) setMetricTime(unitIsTime(v));  // „minút" → čas, kým to človek neprepne sám
                  }} />
                {/* pri stopkách je „čas" dané (merajú ho) — prepínač by len klamal, že sa dá zmeniť */}
                {!metricTimer && (
                  <label style={{ ...st.inlineLabel, marginTop: 8 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><BuzerIcon name="stopwatch" size={16} color="var(--text2)" /> Hodnota je čas</span>
                    <button type="button" role="switch" aria-checked={metricTime}
                      onClick={() => { setMetricTimeSet(true); setMetricTime((v) => !v); }}
                      style={{ ...st.switch, flex: "none", cursor: "pointer", ...(metricTime ? st.switchOn : {}) }}><span style={{ ...st.switchKnob, ...(metricTime ? st.switchKnobOn : {}) }} /></button>
                  </label>
                )}
                {/* STOPKY — rovnaký prepínač aj text ako pri súťaži s vlastným výsledkom.
                    Nahrádzajú odpočítavajúci časovač (pole „Časovač" sa preto skryje) a hodnotu
                    vždy merajú v čase, takže „Hodnota je čas" si zapnú samy. */}
                <label style={{ ...st.inlineLabel, marginTop: 8 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><BuzerIcon name="stopwatch" size={16} color="var(--text2)" /> Stopky merajú výsledok</span>
                  <button type="button" role="switch" aria-checked={metricTimer}
                    onClick={() => setMetricTimer((v) => {
                      const on = !v;
                      if (on) {
                        setMetricTime(true); setMetricTimeSet(true);
                        setTimerMin(0); setTimerSec(0);                    // časovač sa vypína
                        if (!metricLabel.trim()) setMetricLabel("minút");  // stopky merajú minúty
                      }
                      return on;
                    })}
                    style={{ ...st.switch, flex: "none", cursor: "pointer", ...(metricTimer ? st.switchOn : {}) }}><span style={{ ...st.switchKnob, ...(metricTimer ? st.switchKnobOn : {}) }} /></button>
                </label>
                {metricTimer && <div style={{ ...st.taskDesc, marginTop: 6 }}>Spustíš stopky, po zastavení sa nameraný čas pripíše k dnešku (napr. 4:35 alebo 1:23:45).</div>}
                <Field label="Denný cieľ" style={{ marginTop: 8 }}>
                  {metricTime ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <Input style={{ minWidth: 0 }} type="number" min="0" max="600" aria-label="minúty"
                        value={metricTargetMin} onChange={(e) => setMetricTargetMin(e.target.value)} />
                      <span style={st.taskDesc}>min</span>
                      <Input style={{ minWidth: 0 }} type="number" min="0" max="59" aria-label="sekundy"
                        value={metricTargetSec} onChange={(e) => setMetricTargetSec(e.target.value)} />
                      <span style={st.taskDesc}>s</span>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <Input style={{ flex: 1, minWidth: 0 }} type="number" step="any" min="0" placeholder="napr. 30"
                        value={metricTarget} onChange={(e) => setMetricTarget(e.target.value)} />
                      <span style={st.taskDesc}>{metricLabel.trim() || "jednotiek"}</span>
                    </div>
                  )}
                  <div style={{ ...st.taskDesc, marginTop: 6 }}>Nechaj 0, ak cieľ nechceš — vtedy stačí čokoľvek zapísať.</div>
                </Field>
              </>)}
          </Field>
          )}
          {/* ČASOVAČ (odpočítavanie) — pri zapnutých stopkách sa skryje: stopky ho nahrádzajú */}
          {show("timer") && !(metricOn && metricTimer) && !entryOn && (
          <div style={{ display: "flex", gap: 12 }}>
            <Field label="Časovač" style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <Input style={{ minWidth: 0 }} type="number" min="0" max="600"
                  value={timerMin} onChange={(e) => setTimerMin(e.target.value)} aria-label="minúty" />
                <span style={st.taskDesc}>min</span>
                <Input style={{ minWidth: 0 }} type="number" min="0" max="59"
                  value={timerSec} onChange={(e) => setTimerSec(e.target.value)} aria-label="sekundy" />
                <span style={st.taskDesc}>s</span>
              </div>
            </Field>
          </div>
          )}
          {/* SKUPINA (rutina) — v33c: presunutá až ZA body; „i" vysvetlí, čo je skupina */}
          {!oneday && show("group") && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>
                <span className="bz-field__label" style={{ marginBottom: 0 }}>Skupina</span>
                <button type="button" onClick={() => setSkupInfo((v) => !v)} aria-label="Čo je skupina?" title="Čo je skupina?"
                  style={{ width: 16, height: 16, borderRadius: "50%", border: "1.5px solid var(--neutral-text-muted)", background: "transparent", color: "var(--neutral-text-muted)", fontSize: 11, fontWeight: 800, lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, fontFamily: "var(--ff-mono)" }}>i</button>
              </div>
              {skupInfo && <div style={{ ...st.taskDesc, marginBottom: 8 }}>Skupina (rutina) zoskupí súvisiace návyky pod jeden názov — napr. „Ranná rutina". V Dnes sa potom zobrazia spolu ako jedna karta s vlastným pokrokom. Nechaj „samostatný", ak návyk nemá patriť do skupiny.</div>}
              <Select value={catId}
                onChange={(e) => { const v = e.target.value; setCatId(v); if (v !== "__new__") setPendingRoutine(null); else if (!pendingRoutine) setPendingRoutine({ name: "", icon: "folder" }); }}>
                <option value="">— samostatný —</option>
                {(routines || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                <option value="__new__">+ Nová skupina…</option>
              </Select>
              {catId === "__new__" && (
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button type="button" onClick={() => setRoutineIconPick(true)} aria-label="Ikona skupiny"
                    style={{ ...st.field, width: 56, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--text)" }}>
                    <Glyph icon={(pendingRoutine && pendingRoutine.icon) || "folder"} size={22} />
                  </button>
                  <Input style={{ flex: 1, minWidth: 0 }} autoFocus maxLength={40}
                    placeholder="Názov novej skupiny" value={pendingRoutine ? pendingRoutine.name : ""}
                    onChange={(e) => setPendingRoutine({ name: e.target.value, icon: (pendingRoutine && pendingRoutine.icon) || "folder" })} />
                </div>
              )}
              {routineIconPick && (
                <Modal title="Ikona skupiny" icon="palette" onClose={() => setRoutineIconPick(false)}>
                  <IconPicker value={(pendingRoutine && pendingRoutine.icon) || "folder"} onPick={(nm) => { setPendingRoutine((p) => ({ name: (p && p.name) || "", icon: nm })); setRoutineIconPick(false); }} />
                </Modal>
              )}
            </div>
          )}
          {/* OPAKOVANIE — zdieľaný ovládač `RecurrenceField` (ten istý má aj formulár výzvy):
              Každý deň (+ voliteľne „viackrát za deň"), Za obdobie (N× za týždeň/mesiac/pol
              rok/rok — týždeň = weekly_count, ostatné = period), Vlastné dni v týždni. */}
          {!oneday && show("recur") && (
            <RecurrenceField noPerDay={metricOn || entryOn}
              value={{ mode: recurMode, n: recurN, days: recurDays, period: recurPeriod, perDay }}
              onChange={(v) => { setRecurMode(v.mode); setRecurN(v.n); setRecurDays(v.days); setRecurPeriod(v.period); setPerDay(v.perDay); }} />
          )}
          {/* ZAČIATOK — kedy sa návyk prvýkrát objaví na Dnes. Ponúka sa len pri tvorbe:
              pri úprave by posun prepísal `created_at`, teda aj to, odkedy beží história. */}
          {!oneday && (!editing || (src && src.upcoming)) && show("recur") && (
            <Field label="Začiatok">
              <DateField value={startDate} onChange={setStartDate} label="Deň začiatku"
                min={todayKey()} max={shiftDay(todayKey(), START_MAX_AHEAD_DAYS)} />
              {startDate !== todayKey() && (
                <div style={{ ...st.fieldHint, marginTop: 4 }}>
                  Do {fmtDuePretty(startDate)} ho na Dnes neuvidíš — nájdeš ho v sekcii „Začne o…“.
                </div>
              )}
            </Field>
          )}
          {/* v33 §7 — nadväznosť na archivovaný návyk. Len pri TVORBE: pri úprave by zmena
              predchodcu prepísala už započítanú históriu kola. */}
          {kind === "habit" && !src && (
            <PrevRoundField kind="habit" value={prevTaskId} onChange={setPrevTaskId} />
          )}
          {/* TRVANIE (dĺžka behu návyku v dňoch) */}
          {!oneday && show("duration") && (
            <Field label="Trvanie">
              <Select value={durCustom ? "custom" : String(duration)}
                onChange={(e) => { const v = e.target.value; if (v === "custom") { setDurCustom(true); } else { setDurCustom(false); setDuration(Number(v)); } }}>
                <option value="0">Bez limitu</option>
                {DUR_OPTIONS.map(([d, lbl]) => <option key={d} value={String(d)}>{lbl}</option>)}
                <option value="custom">Vlastné…</option>
              </Select>
              {durCustom && (
                <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
                  <Input style={{ width: 90 }} type="number" min="1" max="365"
                    value={duration} onChange={(e) => setDuration(e.target.value)} aria-label="počet dní" />
                  <span style={st.taskDesc}>dní</span>
                </div>
              )}
              {Number(duration) > 0 && <div style={{ ...st.fieldHint, marginTop: 4 }}>Návyk po {duration} dňoch automaticky skončí.</div>}
            </Field>
          )}
          {/* v25 §8 · v32 — PRIPOMENUTIE ako dlaždica (rovnaká ako Progres v profile / Len dnes);
              po zapnutí sa dlaždica zväčší o 24h výber času (HH : MM selecty) */}
          {show("remind") && (
          <div style={{ ...st.toggleCard, flexWrap: "wrap" }}>
            <span style={st.installBannerIcon}><BuzerIcon name="bell" size={18} color="var(--text2)" /></span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: 800, fontSize: 14, display: "block" }}>Pripomenutie</span>
              <span style={st.taskDesc}>Upozorniť ma v čase.</span>
            </span>
            <button type="button" role="switch" aria-checked={!!remindAt} aria-label="Zapnúť pripomenutie"
              onClick={() => setRemindAt(remindAt ? "" : "08:00")}
              style={{ ...st.switch, ...(remindAt ? st.switchOn : {}), flex: "none" }}>
              <span style={{ ...st.switchKnob, ...(remindAt ? st.switchKnobOn : {}) }} />
            </button>
            {!!remindAt && (() => {
              const [hh, mm] = remindAt.split(":");
              const mins = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
              if (mm && !mins.includes(mm)) mins.push(mm);  // zachovaj netypickú minútu z editu
              return (
                <div style={{ flexBasis: "100%", marginTop: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Select value={hh} style={{ width: 96, fontSize: 16 }} aria-label="Hodina"
                      onChange={(e) => setRemindAt(e.target.value + ":" + (mm || "00"))}>
                      {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => <option key={h} value={h}>{h}</option>)}
                    </Select>
                    <span style={{ fontWeight: 700, color: "var(--muted)" }}>:</span>
                    <Select value={mm} style={{ width: 96, fontSize: 16 }} aria-label="Minúta"
                      onChange={(e) => setRemindAt((hh || "08") + ":" + e.target.value)}>
                      {mins.sort().map((m) => <option key={m} value={m}>{m}</option>)}
                    </Select>
                  </div>
                  <div style={{ ...st.fieldHint, marginTop: 6 }}>Upozornenie príde, len ak návyk ešte nie je splnený a v ten deň sa má robiť.</div>
                </div>
              );
            })()}
          </div>
          )}
          {/* v32 — VEREJNÝ PROGRES: opt-in, progres návyku (7 dní + séria) vidno vo verejnom profile */}
          {show("public") && (
          <div style={st.toggleCard}>
            <span style={st.installBannerIcon}><BuzerIcon name="eye" size={18} color="var(--text2)" /></span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: 800, fontSize: 14, display: "block" }}>Progres v profile</span>
              <span style={st.taskDesc}>Posledných 7 dní a séria budú vo verejnom profile.</span>
            </span>
            <button type="button" role="switch" aria-checked={publicProgress} aria-label="Progres v profile"
              onClick={() => setPublicProgress((v) => !v)} style={{ ...st.switch, ...(publicProgress ? st.switchOn : {}) }}>
              <span style={{ ...st.switchKnob, ...(publicProgress ? st.switchKnobOn : {}) }} />
            </button>
          </div>
          )}
          {/* LEN DNES (boost) — create + edit */}
          {show("oneday") && (
            <div style={st.toggleCard}>
              <span style={st.installBannerIcon}><BuzerIcon name="flame" size={18} color="var(--text2)" /></span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 800, fontSize: 14, display: "block" }}>Len dnes</span>
                <span style={st.taskDesc}>Boost — nezopakuje sa zajtra.</span>
              </span>
              <button type="button" role="switch" aria-checked={oneday} aria-label="Len dnes"
                onClick={() => setOneday((v) => !v)} style={{ ...st.switch, ...(oneday ? st.switchOn : {}) }}>
                <span style={{ ...st.switchKnob, ...(oneday ? st.switchKnobOn : {}) }} />
              </button>
            </div>
          )}
        </>
      )}
      {kind === "onetime" && (
        <>
          <Input placeholder="Poznámka"
            value={desc} onChange={(e) => setDesc(e.target.value)} />
          <label style={st.inlineLabel}><span>Dátum</span>
            {/* v26.3 §1 — kalendárový picker (čistý dátum) namiesto natívneho input[type=date] */}
            <DateField value={due} onChange={setDue} allowClear label="Dátum" /></label>
          {session.premium ? (
            <>
              <Input placeholder="Kategória"
                value={category} onChange={(e) => setCategory(e.target.value)} />
              {session.ai && (
                <button type="button" onClick={aiCategory} disabled={catBusy} style={st.aiBtn}><BuzerIcon name="ai" size={14} />
                  {catBusy ? "premýšľam…" : "Navrhni kategóriu"}
                </button>
              )}
              {(categories || []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {categories.map((c) => (
                    <button type="button" key={c} onClick={() => setCategory(c)} className="chip"
                      style={{ ...st.chip, flex: "none", ...(category === c ? st.chipOn : {}) }}>{c}</button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div>
              <input className="field" disabled placeholder="🔒 Kategória — len v Community"
                style={{ ...st.field, width: "100%", background: "var(--field)", color: "var(--muted)", cursor: "not-allowed" }} />
              <a href="/o-aplikacii#cennik" style={{ ...st.taskDesc, color: "var(--price-accent)", textDecoration: "none", display: "inline-block", marginTop: 4 }}>
                Organizuj úlohy do kategórií s Community ›
              </a>
            </div>
          )}
        </>
      )}

      {kind === "reminder" && (
        <>
          {session.ai && !editing && (
            <button type="button" onClick={aiFill} disabled={aiBusy} style={st.aiBtn}><BuzerIcon name="ai" size={14} />
              {aiBusy ? "premýšľam…" : "Navrhni ikonu a poznámku"}
            </button>
          )}
          <Input placeholder="Poznámka"
            value={desc} onChange={(e) => setDesc(e.target.value)} />
          <label style={st.inlineLabel}><span>Termín</span>
            {/* v26.3 §1 — kalendár + čas namiesto natívneho datetime-local (required rieši submit) */}
            <DateTimeField value={due} onChange={setDue} label="Termín" /></label>
          <div>
            <div style={st.fieldHint}>Poslať e-mail vopred:</div>
            <div style={{ display: "flex", gap: 8 }}>
              {OFFSETS.map((o) => {
                const on = offs.includes(o.m);
                return (
                  <button type="button" key={o.m} onClick={() => toggleOff(o.m)} className="chip"
                    style={{ ...st.chip, ...(on ? st.chipOn : {}) }}>{on ? <Chk /> : null}{o.l}{o.m > 0 ? " vopred" : ""}</button>
                );
              })}
            </div>
          </div>
          <label style={st.inlineLabel}><span>Opakovať</span>
            <Select style={{ width: 150 }}
              value={repeat} onChange={(e) => setRepeat(e.target.value)}>
              <option value="none">Jednorázovo</option>
              <option value="daily">Každý deň</option>
              <option value="weekly">Každý týždeň</option>
              <option value="monthly">Každý mesiac</option>
              <option value="yearly">Každý rok</option>
            </Select></label>
          <div>
            <div style={st.fieldHint}>👁 Zdieľať pripomienku s priateľmi:</div>
            {friends.length === 0 ? (
              <div style={st.taskDesc}>Zatiaľ žiadni priatelia — pridaj si ich v záložke Priatelia.</div>
            ) : (
              <><FriendPickerHint />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {friends.map((f) => (
                  <FriendChip key={f.friend_id} friend={f} selected={shareWith.includes(f.friend_id)}
                    onToggle={toggleShare} chipStyle={{ ...st.chip, flex: "none" }} chipOnStyle={st.chipOn} />
                ))}
              </div></>
            )}
          </div>
        </>
      )}

      {err && <div style={st.err}>{err}</div>}

      {inventOpen && (
        <AiCenterDialog onClose={() => { if (!aiBusy) setInventOpen(false); }}>
          <div style={{ padding: "24px 22px 18px", textAlign: "center" }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, margin: "0 auto 14px", background: "var(--ember-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><BuzerIcon name="sparkles" size={24} color="currentColor" /></div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 19, marginBottom: 6, color: "var(--text)" }}>Chceš, aby ti AI vymyslela nový návyk?</div>
            <p style={{ fontSize: 13.5, color: "var(--muted)", margin: 0, lineHeight: 1.45 }}>Na základe tvojich cieľov a rutín navrhne názov, body aj frekvenciu.</p>
          </div>
          <div style={{ display: "flex", gap: 10, padding: "14px 20px 18px" }}>
            <Button variant="ghost" onClick={() => setInventOpen(false)} disabled={aiBusy}>Zrušiť</Button>
            <Button variant="primary" block onClick={doInvent} disabled={aiBusy}><BuzerIcon name="sparkles" size={16} color="currentColor" />{aiBusy ? "Vymýšľam…" : "Vymyslieť"}</Button>
          </div>
        </AiCenterDialog>
      )}
      {lifeCatDialog && (
        <AiCenterDialog onClose={() => setLifeCatDialog(null)}>
          <div style={{ padding: "22px 22px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 13 }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: "var(--ember-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><BuzerIcon name="sparkles" size={22} color="currentColor" /></div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: 14, color: "var(--muted)", marginBottom: 2 }}>AI navrhuje kategóriu</div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 24, color: "var(--text)", display: "inline-flex", alignItems: "center", gap: 8 }}>
                {lifeCatDialog.busy ? "…" : (<>
                  <BuzerIcon name={(LIFE_CAT[lifeCatDialog.category] || {}).icon || "label"} size={22} color="currentColor" />
                  {(LIFE_CAT[lifeCatDialog.category] || {}).full || lifeCatDialog.category}
                </>)}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, padding: "6px 20px 18px" }}>
            <Button variant="ghost" onClick={() => setLifeCatDialog(null)}>Zrušiť</Button>
            <Button variant="primary" block disabled={lifeCatDialog.busy} onClick={() => { if (lifeCatDialog.category) setCategory(lifeCatDialog.category); setLifeCatDialog(null); }}>Aplikovať</Button>
          </div>
        </AiCenterDialog>
      )}
      {ptsDialog && (
        <AiCenterDialog onClose={() => setPtsDialog(null)}>
          <div style={{ padding: "22px 22px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 13 }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: "var(--ember-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><BuzerIcon name="sparkles" size={22} color="currentColor" /></div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: 14, color: "var(--muted)", marginBottom: 2 }}>AI navrhuje</div>
              {/* f-batch #2 — LEN náročnosť, žiadne body: zobrazený základ (5/10/20) ignoruje
                  koeficient série, takže človeku so sériou ukazoval iné číslo, než akú odmenu
                  reálne dostane. Skutočný prepočet vysvetľuje „i" pri štítku náročnosti (§7.3). */}
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 24, color: "var(--text)" }}>{ptsDialog.busy ? "…" : DIFF_SK[ptsDialog.level]}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, padding: "6px 20px 18px" }}>
            <Button variant="ghost" onClick={() => setPtsDialog(null)}>Zrušiť</Button>
            <Button variant="primary" block onClick={() => { if (ptsDialog.level) setDifficulty(ptsDialog.level); setPtsDialog(null); }} disabled={ptsDialog.busy}>Aplikovať</Button>
          </div>
        </AiCenterDialog>
      )}
      <FormFooter submitLabel={busy ? "…" : editing ? "Uložiť" : "Pridať"} submitDisabled={busy || !label.trim()}
        deleteLabel="Vymazať návyk" onDelete={editing && onDelete ? onDelete : undefined} onCancel={close} />
    </form>
    </FormShell>
    </>
  );
}

// Reminder / event form (merged in from the old PlanCreate). AddCard routes
// kind="reminder" here. Shares the FormShell shell, IconColorModal and ds
// primitives with HabitCard — one file, one place to fix.
function PlanForm({ reload, open, onOpenChange, editTask, onEditDone, categories, prefill }) {
  const ed = editTask || null;
  const [icon, setIcon] = useState(ed ? (ed.icon || "") : "");
  const [iconColor, setIconColor] = useState(ed ? (ed.icon_color || "") : "");
  const [iconPick, setIconPick] = useState(false);
  const [label, setLabel] = useState(ed ? (ed.label || "") : "");
  const [desc, setDesc] = useState(ed ? (ed.desc || "") : "");
  const [category, setCategory] = useState(ed ? (ed.category || "") : "");
  const [catBusy] = useState(false);
  const [catDialog, setCatDialog] = useState(null);  // AI category suggestion confirm
  async function openCatDialog() {
    if (!label.trim()) { setErr("Najprv napíš názov."); return; }
    setErr(""); setCatDialog({ busy: true });
    try { const r = await api("/api/ai/suggest-category", { method: "POST", body: { label } }); setCatDialog({ category: (r.category || "").trim() }); }
    catch (e) { setErr(e.message); setCatDialog(null); }
  }
  const [planType, setPlanType] = useState(ed ? (ed.plan_type === "event" ? "event" : "note") : "note");
  const [endAt, setEndAt] = useState(ed && ed.end_at ? ed.end_at.replace(" ", "T") : "");
  const [due, setDue] = useState(ed && ed.due_at ? ed.due_at.replace(" ", "T") : "");  // reminders/events store "YYYY-MM-DD HH:MM" → datetime-local needs a T
  const [remind, setRemind] = useState(ed && ed._kind === "reminder" ? ed.notify_email !== false : false);
  const [notifyPush, setNotifyPush] = useState(ed && ed._kind === "reminder" ? ed.notify_push !== false : false);
  // v27 — pri ÚPRAVE zrkadli reálny uložený lead-time (aj prázdny = žiadny); len nový formulár má default.
  // #4 — nová pripomienka defaultne upozorní „v daný čas" (m:0), nie 2 h vopred (na žiadosť: Martin).
  const [offs, setOffs] = useState(
    ed ? (ed.notify_offsets && ed.notify_offsets.length ? ed.notify_offsets : [])
       : [0]);
  const [repeat, setRepeat] = useState(ed ? (ed.repeat || "none") : "none");
  // vlastné dátumy = ďalšie termíny okrem „Termín" (ktorý drží najbližší/prvý dátum); pri úprave
  // ich naplň zo zoznamu bez toho, čo už je v due_at (aby sa neduplikoval).
  const [customDates, setCustomDates] = useState(
    ed && ed.repeat === "custom" && Array.isArray(ed.custom_dates)
      ? ed.custom_dates.filter((s) => s.replace(" ", "T") !== (ed.due_at || "").replace(" ", "T")).map((s) => s.replace(" ", "T"))
      : []);
  const [share, setShare] = useState(!!(ed && (ed.shared || (ed.share_with && ed.share_with.length) || ed.shared_event_id)));
  const [shareWith, setShareWith] = useState(ed && ed.share_with ? ed.share_with : []);
  const [canEdit, setCanEdit] = useState(false);
  const coOwned = !!(ed && ed.shared_event_id);
  const [friends, setFriends] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if ((open || ed) && (remind || share) && friends.length === 0) {
      api("/api/friends").then((d) => setFriends(d.friends || [])).catch(() => {});
    }
  }, [open, remind, share]);
  useEffect(() => {
    if (open && !ed && prefill) {
      if (prefill.due) setDue(prefill.due);
      if (prefill.type === "event") { setPlanType("event"); setShowMore(true); }
      else if (prefill.due) { setPlanType("note"); setRemind(true); setShowMore(true); }
      if (prefill.category) setCategory(prefill.category);  // v2 — per-category +Pridať prefill
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill && prefill.due, prefill && prefill.type, prefill && prefill.category]);
  function toggleOff(m) { setOffs((p) => p.includes(m) ? p.filter((x) => x !== m) : [...p, m]); }
  function toggleShare(id) { setShareWith((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]); }
  function setDueVal(v) { setDue(v); if (!v) { setRemind(false); setNotifyPush(false); setRepeat("none"); } }
  const [showMore, setShowMore] = useState(!!editTask);
  const showMoreBeforeEvent = useRef(!!editTask);
  async function aiSuggest() {
    return api("/api/ai/suggest-reminder", { method: "POST", body: { label, icons: SUGGEST_ICONS, desc } });
  }
  function reset() {
    setIcon(""); setIconColor(""); setLabel(""); setDesc(""); setDue(""); setEndAt(""); setRemind(false); setNotifyPush(false); setCategory("");
    setOffs([0]); setRepeat("none"); setCustomDates([]); setShare(false); setShareWith([]); setCanEdit(false); setErr(""); setPlanType("note");
  }
  async function submit(e) {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (!ed && share && canEdit && !shareWith.length) { setErr("Vyber aspoň jedného priateľa."); setBusy(false); return; }
      // v26 §3.4 — zdieľanie ide cez share_with na JEDNEJ položke (členovia cez reminder_shares),
      // už žiadne kópie per priateľstvo (starý /api/shared/<fid>/events fan-out zrušený)
      const isEvent = planType === "event";
      if (isEvent && !due) { setErr("Pri udalosti treba termín."); setBusy(false); return; }
      // „Pripomienka" bez termínu = obyčajná poznámka: neblokuj, ulož ako poznámku
      // (kind=reminder, plan_type=note, bez dátumu → bez upozornení a opakovania).
      const noteOnly = !isEvent && !due;
      const isReminder = isEvent || ((remind || notifyPush || repeat !== "none") && !noteOnly);
      const body = isEvent
        ? { kind: "reminder", plan_type: "event", icon, icon_color: iconColor || null, label, desc, due_at: due ? due.replace("T", " ") : "",
            end_at: endAt ? endAt.replace("T", " ") : "", repeat, notify_offsets: (remind || notifyPush) ? offs : [],
            notify_email: remind, notify_push: notifyPush, category, share_with: share ? shareWith : [] }
        : noteOnly
          ? { kind: "reminder", plan_type: "note", icon, icon_color: iconColor || null, label, desc, due_at: "",
              repeat: "none", notify_offsets: [], notify_email: false, notify_push: false, category, share_with: share ? shareWith : [] }
          : isReminder
            ? { kind: "reminder", plan_type: "note", icon, icon_color: iconColor || null, label, desc, due_at: due ? due.replace("T", " ") : "",
                repeat, notify_offsets: (remind || notifyPush) ? offs : [],
                notify_email: remind, notify_push: notifyPush, category, share_with: share ? shareWith : [] }
            : { kind: "onetime", plan_type: "note", icon, icon_color: iconColor || null, label, desc, due_at: due ? due.slice(0, 10) : "", category, share_with: share ? shareWith : [] };
      // vlastné dátumy: pošli celý zoznam (Termín = prvý dátum + ďalšie); server z neho odvodí najbližší due_at
      if (isReminder && repeat === "custom") {
        body.custom_dates = [due, ...customDates].filter(Boolean).map((s) => s.replace("T", " "));
      }
      let newId = null;
      if (ed) {
        if (ed.shared_event_id) {
          // v26 fix — kategória je osobná: ulož ju PRED syncom zdieľanej udalosti (sync mirror
          // tasky maže a nanovo vytvára — starý PATCH šiel na už zmazané id a potichu zlyhal)
          if (ed.id) await api("/api/tasks/" + ed.id, { method: "PATCH", body: { category } }).catch(() => {});
          await api("/api/shared/events/" + ed.shared_event_id, { method: "PATCH",
            body: { icon, label, desc, due_at: due ? due.replace("T", " ") : "",
                    end_at: endAt ? endAt.replace("T", " ") : "", repeat, notify: remind || notifyPush,
                    // v27 — pošli konkrétny lead-time + kanály, nech sa zvolené „vopred“ naozaj uloží
                    notify_offsets: (remind || notifyPush) ? offs : [], notify_push: notifyPush, notify_email: remind } });
        } else {
          await api("/api/tasks/" + ed.id, { method: "PATCH", body });
        }
        onEditDone && onEditDone();
      } else {
        const res = await api("/api/tasks", { method: "POST", body });
        newId = res && res.id;
        reset(); onOpenChange(false);
      }
      reload(newId); setBusy(false);  // v2 — pass the new id so Plán can scroll to + flash it
    } catch (e) { setErr((e.message || "Nastala chyba") + " — skús znova."); setBusy(false); }
  }
  if (!ed && !open) return <button data-tour="add-reminder" onClick={() => onOpenChange(true)} style={st.addToggle}><BuzerIcon name="add" size={16} /> Pridať</button>;
  const inModal = open || !!ed;  // editing also self-wraps as the sheet (matches Add pripomienka)
  const planAi = session.ai && session.premium;
  return (
    <FormShell modal={inModal} title={ed ? "Upraviť" : "Pridať pripomienku"} onClose={() => { if (ed) { onEditDone && onEditDone(); } else { reset(); onOpenChange(false); } }}
      iconNode={<Glyph icon={icon || "bell"} size={20} />} iconStyle={iconChipStyle(iconColor)} onIconClick={() => setIconPick(true)}>
    <form onSubmit={submit} className={inModal ? "bz-modal-form" : undefined} style={inModal ? undefined : { ...st.card, marginTop: 12, ...st.formCol }}>
      <Segmented className="bz-seg--block"
        options={[{ value: "note", label: "Pripomienka" }, { value: "event", label: "Udalosť" }]}
        value={planType}
        onChange={(v) => {
          if (v === "note") { setPlanType("note"); setOffs([0]); setShowMore(showMoreBeforeEvent.current); }
          else { if (planType !== "event") showMoreBeforeEvent.current = showMore; setPlanType("event"); setOffs([]); setShowMore(true); }
        }} />
      <Field label={planType === "event" ? "Názov udalosti *" : "O čo ide? *"}>
        <Input placeholder="Čo treba spraviť?" value={label} onChange={(e) => setLabel(e.target.value)} required />
        {iconPick && (
          <IconColorModal icon={icon} iconColor={iconColor} fallback="bell"
            onIcon={setIcon} onColor={setIconColor} onClose={() => setIconPick(false)}
            onCommit={ed ? async (ic, col) => {
              if (ed.shared_event_id) await api("/api/shared/events/" + ed.shared_event_id, { method: "PATCH", body: { icon: ic } });
              else await api("/api/tasks/" + ed.id, { method: "PATCH", body: { icon: ic, icon_color: col || null } });
              reload && reload();
            } : undefined}
            onAiIcon={planAi ? async () => { if (!label.trim()) { setErr("Najprv napíš názov."); return; } return (await aiSuggest()).icon; } : undefined} />
        )}
      </Field>
      <Field label="Poznámka">
        <Input placeholder="Doplň detail alebo kontext" value={desc} onChange={(e) => setDesc(e.target.value)} />
      </Field>

      {!editTask && planType !== "event" && (
        <button type="button" onClick={() => setShowMore((v) => !v)} style={st.moreToggle}>
          <Icon name="chevron" size={15} color="currentColor" style={{ transform: showMore ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .15s" }} />
          {showMore ? "Menej možností" : "Ďalšie možnosti (upozornenie, kategória…)"}
        </button>
      )}
      {showMore && (<>
      {/* v2 — events are categorizable too (incl. shared events); notes keep the no-notif gate.
          #2h — categorizable also for a SHARED reminder and for any reminder WITH a termín (due),
          regardless of its notification, on create AND edit (the category was hidden before). */}
      {session.premium && (planType === "event" || share || !!due || (!remind && !notifyPush && repeat === "none")) && (
        <div style={st.formField}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <label className="bz-field__label" style={{ margin: 0 }}>Kategória</label>
            {planAi && (
              <button type="button" onClick={openCatDialog} disabled={catDialog && catDialog.busy} title="Navrhni kategóriu (AI)" aria-label="Navrhni kategóriu"
                style={{ border: "none", background: "transparent", color: "var(--accent)", display: "inline-flex", cursor: "pointer", padding: 0 }}>
                {catBusy ? "…" : <BuzerIcon name="sparkles" size={15} color="currentColor" />}</button>
            )}
          </div>
          <input className="field" style={st.field} placeholder="napr. Práca, Nákupy, Domov"
            value={category} onChange={(e) => setCategory(e.target.value)} />
          {(categories || []).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 10 }}>
              {categories.map((c) => {
                const on = category === c;
                return (
                  <button type="button" key={c} onClick={() => setCategory(c)}
                    style={{ borderRadius: "var(--radius-pill)", padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "var(--ff-ui)", WebkitTextFillColor: "currentColor",
                      ...(on ? { background: "var(--brand-primary-surface)", color: "var(--brand-primary)", border: "1.5px solid var(--brand-primary-border)" }
                             : { background: "var(--neutral-surface-2)", color: "var(--neutral-text-main)", border: "1.5px solid var(--neutral-border-strong)" }) }}>{c}</button>
                );
              })}
            </div>
          )}
        </div>
      )}
      <div style={st.formField}>
        <label className="bz-field__label">{planType === "event" ? "Termín *" : "Termín"}</label>
        {/* v26.3 §1 — kalendár + čas namiesto natívneho datetime-local */}
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <DateTimeField value={due} onChange={setDueVal} label="Termín" allowClear={planType !== "event"} />
        </div>
        {planType !== "event" && !due && <div style={{ ...st.fieldHint, marginTop: 6 }}>Bez termínu sa uloží ako poznámka. Zapni upozornenie, ak chceš pripomienku.</div>}
        {planType === "event" && !due && <div style={{ ...st.fieldHint, marginTop: 6, color: "var(--danger)" }}>Udalosť potrebuje termín.</div>}
      </div>
      {planType === "event" && due && (
        <div style={st.formField}>
          <label className="bz-field__label"><BuzerIcon name="clock" size={13} color="var(--muted)" style={{ verticalAlign: "-2px", marginRight: 5 }} />Koniec (trvanie)</label>
          {/* v26.3 §1 — kalendár + čas; Koniec nedovolí deň pred Termínom (min=due) */}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <DateTimeField value={endAt} onChange={setEndAt} min={due} label="Koniec" allowClear timeOptional />
          </div>
        </div>
      )}
      {due && (
        <div style={st.formField}>
          <label style={{ ...st.inlineLabel, margin: 0 }}><span>Opakovať</span>
            <select className="field" style={{ ...st.field, width: 150 }}
              value={repeat} onChange={(e) => setRepeat(e.target.value)}>
              <option value="none">Jednorázovo</option>
              <option value="daily">Každý deň</option>
              <option value="weekly">Každý týždeň</option>
              <option value="monthly">Každý mesiac</option>
              <option value="yearly">Každý rok</option>
              <option value="custom">Vlastné dátumy</option>
            </select></label>
          {repeat === "custom" && (
            <div style={{ marginTop: 10 }}>
              <label className="bz-field__label">Ďalšie dátumy</label>
              {customDates.map((cd, i) => (
                <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                  <DateTimeField value={cd} onChange={(v) => setCustomDates((a) => a.map((x, j) => (j === i ? v : x)))} label="Dátum" />
                  <button type="button" aria-label="Odobrať dátum" onClick={() => setCustomDates((a) => a.filter((_, j) => j !== i))}
                    style={{ border: "none", background: "transparent", color: "var(--neutral-text-muted)", cursor: "pointer", display: "inline-flex", padding: 4 }}>
                    <BuzerIcon name="close" size={16} color="currentColor" />
                  </button>
                </div>
              ))}
              <button type="button" style={st.moreToggle}
                onClick={() => setCustomDates((a) => [...a, a.length ? a[a.length - 1] : due])}>
                <Icon name="add" size={15} color="currentColor" /> Pridať dátum
              </button>
              <div style={{ ...st.fieldHint, marginTop: 4 }}>„Termín“ je prvý dátum; pridaj ďalšie konkrétne dátumy. Pripomienka sa sama posúva cez ne, čas sa predvyplní z posledného.</div>
            </div>
          )}
        </div>
      )}

      {due && (
        <div style={st.remindGroup}>
          <div style={st.fieldHint}>Pripomienka</div>
          <div style={{ display: "flex", gap: 8 }}>
            {[["mail", "E-mailom", remind, () => setRemind((v) => !v)], ["bell", "V aplikácii", notifyPush, () => setNotifyPush((v) => !v)]].map(([ic, lbl, on, tog]) => (
              <button type="button" key={lbl} onClick={tog}
                style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderRadius: "var(--radius-md)", padding: "10px 4px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", outline: "none", fontFamily: "var(--ff-ui)", WebkitTextFillColor: "currentColor",
                  ...(on ? { background: "var(--brand-primary-surface)", color: "var(--brand-primary)", border: "1.5px solid var(--brand-primary-border)" }
                         : { background: "var(--neutral-surface-2)", color: "var(--neutral-text-muted)", border: "1.5px solid var(--neutral-border-strong)" }) }}>
                <Icon name={ic} size={15} color="currentColor" /> {lbl}</button>
            ))}
          </div>
          {(remind || notifyPush) && (
            <div style={{ marginTop: 8 }}>
              <div style={st.fieldHint}>Pripomenúť vopred:</div>
              <div style={{ display: "flex", gap: 8 }}>
                {OFFSETS.map((o) => {
                  const on = offs.includes(o.m);
                  return (
                    <button type="button" key={o.m} onClick={() => toggleOff(o.m)} className="chip"
                      style={{ ...st.chip, ...(on ? st.chipOn : {}) }}>{on ? <Chk /> : null}{o.l}{o.m > 0 ? " vopred" : ""}</button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={st.shareWrap}>
        <button type="button" onClick={() => setShare((v) => !v)} style={st.shareHead}>
          <span style={st.installBannerIcon}><BuzerIcon name="people" size={18} color="var(--text2)" /></span>
          <span style={{ flex: 1, minWidth: 0, fontWeight: 800, fontSize: 14 }}>{coOwned ? "Zdieľané" : "Zdieľať s priateľom"}</span>
          <span style={{ ...st.routineChevron, transform: share ? "rotate(90deg)" : "rotate(0deg)" }}><Icon name="chevron" size={18} color="var(--muted)" /></span>
        </button>
        {share && (
          <div style={st.shareBody}>
            {coOwned ? (
              <div style={st.taskDesc}>
                {ed.shared_event_is_owner
                  ? <>Zdieľané s <strong>{ed.shared_event_peer || "priateľom"}</strong> — obaja to môžete upravovať a o zmenách príde e-mail.</>
                  : <>Zdieľa s tebou <strong>{ed.shared_event_peer || "priateľ"}</strong> — obaja to môžete upravovať a o zmenách príde e-mail.</>}
              </div>
            ) : friends.length === 0 ? (
              <div style={st.taskDesc}>Zatiaľ žiadni priatelia — pridaj si ich v záložke Priatelia.</div>
            ) : (
              <><FriendPickerHint />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {friends.map((f) => (
                  <FriendChip key={f.friend_id} friend={f} selected={shareWith.includes(f.friend_id)} onToggle={toggleShare}
                    chipStyle={{ borderRadius: "var(--radius-pill)", padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "var(--ff-ui)", WebkitTextFillColor: "currentColor",
                      background: "var(--neutral-surface-2)", color: "var(--neutral-text-main)", border: "1.5px solid var(--neutral-border-strong)" }}
                    chipOnStyle={{ background: "var(--brand-primary-surface)", color: "var(--brand-primary)", border: "1.5px solid var(--brand-primary-border)" }} />
                ))}
              </div></>
            )}
            {!ed && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 800, fontSize: 14, display: "block" }}>Môžu aj upravovať</span>
                  <span style={st.taskDesc}>{canEdit
                    ? "Zdieľané — obaja to môžete meniť, o zmenách príde e-mail."
                    : "Len na čítanie — priateľ to uvidí, ale nemôže meniť."}</span>
                </span>
                <button type="button" role="switch" aria-checked={canEdit} aria-label="Môžu aj upravovať"
                  onClick={() => setCanEdit((v) => !v)} style={{ ...st.switch, ...(canEdit ? st.switchOn : {}), flex: "none" }}>
                  <span style={{ ...st.switchKnob, ...(canEdit ? st.switchKnobOn : {}) }} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      </>)}

      {err && <div style={st.err}>{err}</div>}

      {catDialog && (
        <AiCenterDialog onClose={() => setCatDialog(null)}>
          <div style={{ padding: "22px 22px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 13 }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: "var(--ember-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><BuzerIcon name="sparkles" size={22} color="currentColor" /></div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: 14, color: "var(--muted)", marginBottom: 2 }}>AI navrhuje kategóriu</div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 24, color: "var(--text)" }}>{catDialog.busy ? "…" : (catDialog.category || "—")}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, padding: "6px 20px 18px" }}>
            <Button variant="ghost" onClick={() => setCatDialog(null)}>Zrušiť</Button>
            <Button variant="primary" block disabled={catDialog.busy || !catDialog.category} onClick={() => { if (catDialog.category) setCategory(catDialog.category); setCatDialog(null); }}>Aplikovať</Button>
          </div>
        </AiCenterDialog>
      )}
      <FormFooter submitLabel={busy ? "…" : (ed ? "Uložiť" : "Pridať")} submitDisabled={busy || !label.trim()}
        onCancel={() => ed ? (onEditDone && onEditDone()) : (reset(), onOpenChange(false))} />
    </form>
    </FormShell>
  );
}
